import { type RefObject, useEffect } from "react";
import { scrollProgress } from "./progress";

/**
 * Follows how far a tall section has been scrolled through (`scrollProgress`) and hands it to `write`, at most once a
 * frame and without a React render: `write` sets custom properties with `style.setProperty`, which the CSP allows
 * where an injected `<style>` is refused (ADR-074, ADR-286). Without movement the section stands at `stillAt`, and
 * `write` still hears the scroll, for what depends on where the section is (the top bar over Guestlist's night).
 */
export function useScrollProgress<T extends HTMLElement>(
  ref: RefObject<T | null>,
  write: (element: T, progress: number) => void,
  { still, stillAt }: { still: boolean; stillAt: number },
) {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const { top, height } = element.getBoundingClientRect();
      write(element, still ? stillAt : scrollProgress(top, height, window.innerHeight));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [ref, write, still, stillAt]);
}
